import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CrosshairMode } from 'lightweight-charts';
import api from '../services/api';

let tvScriptLoadingPromise;

// Map leveraged/special tokens to their base pairs for charting
const getChartSymbol = (symbol) => {
    // Remove leveraged token suffixes and map to base pair
    const leveragedPatterns = [
        { pattern: /(.+)(DOWN|UP|BULL|BEAR)(USDT)$/i, replace: '$1$3' },
        { pattern: /^1000(.+)$/i, replace: '$1' }, // 1000SATS -> SATS
    ];

    for (const { pattern, replace } of leveragedPatterns) {
        if (pattern.test(symbol)) {
            return symbol.replace(pattern, replace);
        }
    }
    return symbol;
};

// Calculate Simple Moving Average
const calculateSMA = (data, period) => {
    const result = [];
    for (let i = 0; i < data.length; i++) {
        if (i < period - 1) {
            result.push({ time: data[i].time, value: null });
        } else {
            let sum = 0;
            for (let j = 0; j < period; j++) {
                sum += data[i - j].close;
            }
            result.push({ time: data[i].time, value: sum / period });
        }
    }
    return result.filter(d => d.value !== null);
};

// Calculate RSI
const calculateRSI = (data, period = 14) => {
    if (data.length < period + 1) return [];

    const changes = [];
    for (let i = 1; i < data.length; i++) {
        changes.push(data[i].close - data[i - 1].close);
    }

    const result = [];
    let avgGain = 0;
    let avgLoss = 0;

    // First RSI calculation
    for (let i = 0; i < period; i++) {
        if (changes[i] > 0) avgGain += changes[i];
        else avgLoss += Math.abs(changes[i]);
    }
    avgGain /= period;
    avgLoss /= period;

    for (let i = period; i < changes.length; i++) {
        const change = changes[i];
        if (change > 0) {
            avgGain = (avgGain * (period - 1) + change) / period;
            avgLoss = (avgLoss * (period - 1)) / period;
        } else {
            avgGain = (avgGain * (period - 1)) / period;
            avgLoss = (avgLoss * (period - 1) + Math.abs(change)) / period;
        }

        const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
        const rsi = 100 - (100 / (1 + rs));
        result.push({ time: data[i + 1].time, value: rsi });
    }

    return result;
};

// Professional chart component using Lightweight Charts (TradingView library)
const LightweightChart = ({ symbol, klines, interval, setInterval }) => {
    const chartContainerRef = useRef(null);
    const rsiContainerRef = useRef(null);
    const chartRef = useRef(null);
    const rsiChartRef = useRef(null);
    const candlestickSeriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);
    const maSeriesRef = useRef(null);
    const rsiSeriesRef = useRef(null);
    const priceLineRef = useRef(null);
    const [ohlcData, setOhlcData] = useState({ open: 0, high: 0, low: 0, close: 0, change: 0 });
    const [maValue, setMaValue] = useState(null);
    const [rsiValue, setRsiValue] = useState(null);

    useEffect(() => {
        if (!chartContainerRef.current || !rsiContainerRef.current) return;

        // Create main chart
        const chart = createChart(chartContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: '#181a20' },
                textColor: '#848e9c',
            },
            grid: {
                vertLines: { color: '#2b3139' },
                horzLines: { color: '#2b3139' },
            },
            crosshair: {
                mode: CrosshairMode.Normal,
                vertLine: {
                    color: '#758696',
                    width: 1,
                    style: 2,
                    labelBackgroundColor: '#2b3139',
                },
                horzLine: {
                    color: '#758696',
                    width: 1,
                    style: 2,
                    labelBackgroundColor: '#2b3139',
                },
            },
            rightPriceScale: {
                borderColor: '#2b3139',
                scaleMargins: {
                    top: 0.1,
                    bottom: 0.25,
                },
            },
            timeScale: {
                borderColor: '#2b3139',
                timeVisible: true,
                secondsVisible: false,
            },
            handleScroll: {
                mouseWheel: true,
                pressedMouseMove: true,
                horzTouchDrag: true,
                vertTouchDrag: true,
            },
            handleScale: {
                axisPressedMouseMove: true,
                mouseWheel: true,
                pinch: true,
            },
        });

        chartRef.current = chart;

        // Add candlestick series
        const candlestickSeries = chart.addCandlestickSeries({
            upColor: '#0ecb81',
            downColor: '#f6465d',
            borderUpColor: '#0ecb81',
            borderDownColor: '#f6465d',
            wickUpColor: '#0ecb81',
            wickDownColor: '#f6465d',
        });
        candlestickSeriesRef.current = candlestickSeries;

        // Add MA line series
        const maSeries = chart.addLineSeries({
            color: '#f0b90b',
            lineWidth: 2,
            title: 'MA 9',
            priceLineVisible: false,
            lastValueVisible: true,
        });
        maSeriesRef.current = maSeries;

        // Add volume series
        const volumeSeries = chart.addHistogramSeries({
            color: '#26a69a',
            priceFormat: {
                type: 'volume',
            },
            priceScaleId: 'volume',
            scaleMargins: {
                top: 0.85,
                bottom: 0,
            },
        });
        volumeSeriesRef.current = volumeSeries;

        // Create RSI chart
        const rsiChart = createChart(rsiContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: '#181a20' },
                textColor: '#848e9c',
            },
            grid: {
                vertLines: { color: '#2b3139' },
                horzLines: { color: '#2b3139' },
            },
            crosshair: {
                mode: CrosshairMode.Normal,
                vertLine: {
                    color: '#758696',
                    width: 1,
                    style: 2,
                    labelBackgroundColor: '#2b3139',
                },
                horzLine: {
                    color: '#758696',
                    width: 1,
                    style: 2,
                    labelBackgroundColor: '#2b3139',
                },
            },
            rightPriceScale: {
                borderColor: '#2b3139',
                scaleMargins: {
                    top: 0.1,
                    bottom: 0.1,
                },
            },
            timeScale: {
                borderColor: '#2b3139',
                timeVisible: false,
                visible: false,
            },
            handleScroll: false,
            handleScale: false,
        });
        rsiChartRef.current = rsiChart;

        // Add RSI series
        const rsiSeries = rsiChart.addLineSeries({
            color: '#9b59b6',
            lineWidth: 2,
            title: 'RSI 14',
            priceLineVisible: false,
        });
        rsiSeriesRef.current = rsiSeries;

        // Add RSI levels (70, 30)
        rsiSeries.createPriceLine({ price: 70, color: '#f6465d', lineWidth: 1, lineStyle: 2, axisLabelVisible: true });
        rsiSeries.createPriceLine({ price: 30, color: '#0ecb81', lineWidth: 1, lineStyle: 2, axisLabelVisible: true });
        rsiSeries.createPriceLine({ price: 50, color: '#758696', lineWidth: 1, lineStyle: 2, axisLabelVisible: false });

        // Sync time scales
        chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
            if (range) {
                rsiChart.timeScale().setVisibleLogicalRange(range);
            }
        });

        rsiChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
            if (range) {
                chart.timeScale().setVisibleLogicalRange(range);
            }
        });

        // Handle resize
        const handleResize = () => {
            if (chartContainerRef.current) {
                chart.applyOptions({
                    width: chartContainerRef.current.clientWidth,
                    height: chartContainerRef.current.clientHeight,
                });
            }
            if (rsiContainerRef.current) {
                rsiChart.applyOptions({
                    width: rsiContainerRef.current.clientWidth,
                    height: rsiContainerRef.current.clientHeight,
                });
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
            chart.remove();
            rsiChart.remove();
        };
    }, []);

    // Update chart data when klines change
    useEffect(() => {
        if (!candlestickSeriesRef.current || !volumeSeriesRef.current || !klines || klines.length === 0) return;

        // Format data for Lightweight Charts
        const candleData = klines.map(k => ({
            time: k.time,
            open: k.open,
            high: k.high,
            low: k.low,
            close: k.close,
        }));

        const volumeData = klines.map(k => ({
            time: k.time,
            value: k.volume || 0,
            color: k.close >= k.open ? 'rgba(14, 203, 129, 0.5)' : 'rgba(246, 70, 93, 0.5)',
        }));

        candlestickSeriesRef.current.setData(candleData);
        volumeSeriesRef.current.setData(volumeData);

        // Calculate and set MA data
        if (maSeriesRef.current) {
            const maData = calculateSMA(klines, 9);
            maSeriesRef.current.setData(maData);
            if (maData.length > 0) {
                setMaValue(maData[maData.length - 1].value);
            }
        }

        // Calculate and set RSI data
        if (rsiSeriesRef.current && rsiChartRef.current) {
            const rsiData = calculateRSI(klines, 14);
            rsiSeriesRef.current.setData(rsiData);
            if (rsiData.length > 0) {
                setRsiValue(rsiData[rsiData.length - 1].value);
            }
            rsiChartRef.current.timeScale().fitContent();
        }

        // Update current price line
        if (candlestickSeriesRef.current && klines.length > 0) {
            const lastCandle = klines[klines.length - 1];
            const prevCandle = klines.length > 1 ? klines[klines.length - 2] : lastCandle;
            const isUp = lastCandle.close >= prevCandle.close;

            // Remove old price line
            if (priceLineRef.current) {
                candlestickSeriesRef.current.removePriceLine(priceLineRef.current);
            }

            // Add new price line
            priceLineRef.current = candlestickSeriesRef.current.createPriceLine({
                price: lastCandle.close,
                color: isUp ? '#0ecb81' : '#f6465d',
                lineWidth: 1,
                lineStyle: 0,
                axisLabelVisible: true,
                title: '',
            });

            // Set initial OHLC data
            const change = ((lastCandle.close - lastCandle.open) / lastCandle.open) * 100;
            setOhlcData({
                open: lastCandle.open,
                high: lastCandle.high,
                low: lastCandle.low,
                close: lastCandle.close,
                change: change,
            });
        }

        // Fit content
        if (chartRef.current) {
            chartRef.current.timeScale().fitContent();
        }

        // Subscribe to crosshair move for OHLC updates
        if (chartRef.current && candlestickSeriesRef.current) {
            chartRef.current.subscribeCrosshairMove((param) => {
                if (param.time && param.seriesData) {
                    const candleDataPoint = param.seriesData.get(candlestickSeriesRef.current);
                    if (candleDataPoint) {
                        const change = ((candleDataPoint.close - candleDataPoint.open) / candleDataPoint.open) * 100;
                        setOhlcData({
                            open: candleDataPoint.open,
                            high: candleDataPoint.high,
                            low: candleDataPoint.low,
                            close: candleDataPoint.close,
                            change: change,
                        });
                    }

                    // Update MA value on hover
                    if (maSeriesRef.current) {
                        const maDataPoint = param.seriesData.get(maSeriesRef.current);
                        if (maDataPoint && maDataPoint.value) {
                            setMaValue(maDataPoint.value);
                        }
                    }
                }
            });
        }
    }, [klines]);

    const formatPrice = (price) => {
        if (price === undefined || price === null) return '-';
        if (price < 0.01) return price.toFixed(6);
        if (price < 1) return price.toFixed(4);
        return price.toFixed(2);
    };

    return (
        <div className="w-full h-full flex flex-col">
            {/* Toolbar with OHLC */}
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-[#1e2329] border-b border-gray-700">
                <span className="text-sm font-bold text-white">{symbol}</span>
                <div className="flex gap-1 ml-2">
                    {['1m', '5m', '15m', '1h', '4h', '1d'].map(int => (
                        <button
                            key={int}
                            onClick={() => setInterval(int)}
                            className={`px-2 py-1 text-xs rounded ${interval === int ? 'bg-[#f0b90b] text-black font-bold' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                        >
                            {int}
                        </button>
                    ))}
                </div>
                <div className="flex gap-3 ml-4 text-xs">
                    <span className="text-gray-400">O<span className="text-white ml-1">{formatPrice(ohlcData.open)}</span></span>
                    <span className="text-gray-400">H<span className="text-[#0ecb81] ml-1">{formatPrice(ohlcData.high)}</span></span>
                    <span className="text-gray-400">L<span className="text-[#f6465d] ml-1">{formatPrice(ohlcData.low)}</span></span>
                    <span className="text-gray-400">C<span className={`ml-1 ${ohlcData.change >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>{formatPrice(ohlcData.close)}</span></span>
                    <span className={`${ohlcData.change >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'}`}>{ohlcData.change >= 0 ? '+' : ''}{ohlcData.change.toFixed(2)}%</span>
                </div>
            </div>
            {/* Indicators Info */}
            <div className="flex gap-4 px-3 py-1 bg-[#181a20] text-xs border-b border-gray-800">
                <span className="text-[#f0b90b]">MA(9): {maValue ? formatPrice(maValue) : '-'}</span>
                <span className="text-[#9b59b6]">RSI(14): {rsiValue ? rsiValue.toFixed(2) : '-'}</span>
            </div>
            {/* Main Chart */}
            <div ref={chartContainerRef} className="flex-1" style={{ minHeight: '250px' }} />
            {/* RSI Chart */}
            <div className="px-3 py-1 bg-[#181a20] text-xs text-gray-400 border-t border-gray-800">RSI(14)</div>
            <div ref={rsiContainerRef} style={{ height: '80px' }} />
        </div>
    );
};

export default function TradingViewWidget({ symbol, isCustom = false }) {
    const onLoadScriptRef = useRef();
    const widgetRef = useRef(null);
    const chartSymbol = getChartSymbol(symbol);
    const [klines, setKlines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [chartInterval, setChartInterval] = useState('1h');

    // Fetch klines for custom tokens
    useEffect(() => {
        if (isCustom) {
            const fetchKlines = async () => {
                setLoading(true);
                try {
                    const res = await api.get(`/markets/${symbol}/klines?interval=${chartInterval}&limit=100`);
                    setKlines(res.data.klines || []);
                } catch (error) {
                    console.error('Failed to fetch custom token klines:', error);
                    setKlines([]);
                }
                setLoading(false);
            };
            fetchKlines();

            // Refresh every 30 seconds
            const intervalId = window.setInterval(fetchKlines, 30000);
            return () => window.clearInterval(intervalId);
        }
    }, [symbol, isCustom, chartInterval]);

    useEffect(
        () => {
            if (isCustom) return; // Skip TradingView for custom tokens

            onLoadScriptRef.current = createWidget;

            if (!tvScriptLoadingPromise) {
                tvScriptLoadingPromise = new Promise((resolve) => {
                    const script = document.createElement('script');
                    script.id = 'tradingview-widget-loading-script';
                    script.src = 'https://s3.tradingview.com/tv.js';
                    script.type = 'text/javascript';
                    script.onload = resolve;

                    document.head.appendChild(script);
                });
            }

            // Use setTimeout to ensure DOM container exists before creating widget
            tvScriptLoadingPromise.then(() => {
                setTimeout(() => {
                    if (onLoadScriptRef.current) {
                        onLoadScriptRef.current();
                    }
                }, 100);
            });

            return () => {
                onLoadScriptRef.current = null;
                // Clear widget container on symbol change
                const containerId = `tradingview_widget_${symbol}`;
                const container = document.getElementById(containerId);
                if (container) container.innerHTML = '';
            };

            function createWidget() {
                const containerId = `tradingview_widget_${symbol}`;
                const container = document.getElementById(containerId);

                console.log('Creating TradingView widget for:', symbol, 'chartSymbol:', chartSymbol, 'Container found:', !!container);

                if (container && 'TradingView' in window) {
                    // Clear previous widget
                    container.innerHTML = '';

                    try {
                        widgetRef.current = new window.TradingView.widget({
                            autosize: true,
                            symbol: `BINANCE:${chartSymbol}`,
                            interval: "D",
                            timezone: "Etc/UTC",
                            theme: "dark",
                            style: "1",
                            locale: "en",
                            enable_publishing: false,
                            allow_symbol_change: true,
                            container_id: containerId,
                            hide_side_toolbar: false,
                            studies: [
                                "MASimple@tv-basicstudies",
                                "RSI@tv-basicstudies"
                            ]
                        });
                        console.log('TradingView widget created successfully for', symbol);
                    } catch (error) {
                        console.error('Error creating TradingView widget:', error);
                    }
                } else {
                    console.warn('TradingView container not found or library not loaded', { containerId, container: !!container, lib: 'TradingView' in window });
                    // Retry after DOM is ready
                    if (!container) {
                        setTimeout(createWidget, 200);
                    }
                }
            }
        },
        [symbol, chartSymbol, isCustom]
    );

    // Show professional chart for custom tokens
    if (isCustom) {
        if (loading && klines.length === 0) {
            return (
                <div className="w-full h-full flex items-center justify-center bg-[#181a20]">
                    <div className="text-gray-400">Loading chart data...</div>
                </div>
            );
        }
        return <LightweightChart symbol={symbol} klines={klines} interval={chartInterval} setInterval={setChartInterval} />;
    }

    return (
        <div className='tradingview-widget-container' style={{ height: "100%", width: "100%" }}>
            <div id={`tradingview_widget_${symbol}`} style={{ height: "100%", width: "100%" }} />
            {chartSymbol !== symbol && (
                <div className="absolute bottom-2 left-2 bg-yellow-500/20 text-yellow-400 text-xs px-2 py-1 rounded">
                    Showing {chartSymbol} chart (base pair)
                </div>
            )}
        </div>
    );
}

